import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogDetail = async () => {
      try {
        const response = await fetch(`/api/blogs/detail/${slug}`);
        const result = await response.json();
        
        if (response.ok && result.success) {
          setBlog(result.data);
        } else {
          setError(result.message || 'The article you are looking for does not exist.');
        }
      } catch (err) {
        console.error('Data Stream Error:', err);
        setError('Could not establish connection link with backend core endpoint.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogDetail();
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-slate-50 text-slate-500 min-h-screen flex flex-col items-center justify-center font-mono text-xs animate-pulse">
        <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        Opening article channel...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-50 min-h-screen flex flex-col items-center justify-center p-6 font-sans">
        <div className="bg-white border border-slate-200 p-8 rounded-2xl max-w-md w-full text-center shadow-xl">
          <div className="text-3xl mb-3">📄</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Article Not Found</h2>
          <p className="text-slate-500 text-sm mb-6">{error}</p>
          <Link to="/blogs" className="inline-block text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-5 py-3 rounded-xl transition-all shadow-md shadow-indigo-100">
            ← Back to Article List
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-800 min-h-screen py-10 font-sans antialiased">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        
        {/* TOP NAVIGATION */}
        <div className="mb-8 flex justify-between items-center">
          <Link 
            to="/blogs" 
            className="text-xs font-bold text-slate-600 hover:text-indigo-600 bg-white border border-slate-200/80 px-4 py-2.5 rounded-xl transition-all shadow-sm flex items-center gap-2"
          >
            <span>←</span> Back to Articles
          </Link>
          <span className="text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-1.5 rounded-xl tracking-wide uppercase text-[10px]">
            {blog.tag}
          </span>
        </div>

        {/* HERO HEADER DETAILS */}
        <header className="mb-8 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 text-xs font-semibold text-slate-400 mb-3">
            <span>📅 Published on</span>
            <time className="text-slate-600">
              {new Date(blog.created_at).toLocaleDateString('en-US', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </time>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight">
            {blog.title}
          </h1>
        </header>

        {/* FEATURED BANNER IMAGE VIEW */}
        {blog.top_image && (
          <div className="w-full aspect-[21/9] rounded-2xl overflow-hidden shadow-md mb-10 border border-slate-200 bg-slate-200">
            <img 
              src={`${blog.top_image}`} 
              alt={blog.title} 
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* ISOLATED HTML DISPLAY WORKSPACE */}
        <main className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-2 md:p-4">

          <iframe
            title={blog.title}
            srcDoc={blog.description} // Seamlessly mounts your fullPage text string markup safely
            sandbox="allow-same-origin"
            className="w-full border-0 block min-h-[500px]"
            onLoad={(e) => {
              const obj = e.target;
              if (obj && obj.contentWindow) {
                // Read inside parameters to remove borders or backgrounds from fullPage body defaults
                const innerDoc = obj.contentWindow.document;
                if(innerDoc.body) {
                  innerDoc.body.style.margin = '20px';
                  innerDoc.body.style.fontFamily = 'system-ui, -apple-system, sans-serif';
                }
                // Automatically scale layout canvas container height dynamic boundary frames
                obj.style.height = innerDoc.documentElement.scrollHeight + 60 + 'px';
              }
            }}
          />
        </main>

      </div>
    </div>
  );
};

export default BlogDetail;
